import { type RemoteIntroPromoCohort } from '@heidi/growth/contracts';
import {
  Shared__enums__enums__Period as Period,
  SubscriptionTier,
} from '@heidi/schema/ml-scribe';
import { useQueryJoinedTeams, useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

import {
  useIsRemoteIntroPromoFreeEnabled,
  useIsRemoteIntroPromoMonthlyEnabled,
  useIsRemoteIntroPromoYearlyEnabled,
} from '../../../flags';
import { remoteIntroPromoDevForceCohortAtom } from '../atoms/remote-intro-promo-atoms';
import { normalizeRemotePromoCountry } from '../pricing';
import type { RemoteIntroPromoHost } from '../remote-intro-promo-host.types';

const MIN_SESSIONS = 10;

/** Offer cuts off at 23:59 UTC on 31 May 2026 (month index is 0-based). */
const REMOTE_PROMO_END_UTC = Date.UTC(2026, 4, 31, 23, 59, 59);

const SUBSCRIBER_TIERS: ReadonlyArray<SubscriptionTier> = [
  SubscriptionTier.PRO,
  SubscriptionTier.CLINICIAN_PRO,
];

/**
 * Returns the cohort the user matches, or `null` if none. Cohorts are
 * mutually exclusive by tier × billing-cycle, so at most one is ever
 * eligible per user. Force-show (via `__remoteIntroPromo.forceShow(cohort)`
 * in non-prod builds) overrides every gate.
 */
export function useRemoteIntroPromoEligibility(
  host: RemoteIntroPromoHost
): RemoteIntroPromoCohort | null {
  const monthlyFlag = useIsRemoteIntroPromoMonthlyEnabled();
  const yearlyFlag = useIsRemoteIntroPromoYearlyEnabled();
  const freeFlag = useIsRemoteIntroPromoFreeEnabled();
  const { data: user } = useQueryUser();
  const { userTier } = host.useUserTier();
  const { data: billingInfo } = host.useUserBillingInfo();
  const { data: joinedTeams } = useQueryJoinedTeams();
  // This hook runs in always-mounted chrome (the sidebar footer / global promo
  // modal). `useQueryCombinedSessions` gates itself off on the new session list
  // (served by the v2.2 endpoint), so this eligibility check fires no legacy
  // `/sessions` request there — and dedupes with the legacy list's own query
  // when the new list is off.
  const { sessions } = host.useQueryCombinedSessions();
  const devForceCohort = useAtomValue(remoteIntroPromoDevForceCohortAtom);

  // ON-PREM-GATE: the promo sells the Heidi Remote hardware — never offer it on
  // the airgapped on-prem build. Checked ahead of the dev force-show hatch so it
  // can't be previewed there either.
  if (host.isOnPrem) return null;

  // Dev/staging escape hatch — short-circuits every gate so QA can preview
  // any cohort. The atom can only be flipped via the non-prod-gated
  // `window.__remoteIntroPromo.forceShow(cohort)` helper.
  if (devForceCohort) return devForceCohort;

  // Hard cutoff: the offer ends at 23:59 UTC on 31 May 2026.
  if (Date.now() >= REMOTE_PROMO_END_UTC) return null;

  // Shared gates that apply to every cohort. Heidi stores user.country as
  // the full name ("Australia"), not ISO — normalise before checking.
  if (!normalizeRemotePromoCountry(user?.country)) return null;
  if (user?.is_managed) return null;

  // Fail closed while team data is unresolved — defaulting `.length` to 0
  // would briefly leak the promo to team users during the initial query
  // load window.
  if (!joinedTeams) return null;
  if (joinedTeams.joined_teams.length > 0) return null;

  if ((sessions?.length ?? 0) <= MIN_SESSIONS) return null;

  // `subscription_status === 'active'` from Figma maps to "user has a real
  // tier and is not in any trial state". The FE billing schema doesn't
  // expose `subscription_status` directly, so we synthesise it here.
  const isTrialling = Boolean(
    billingInfo?.is_in_free_trial ||
    billingInfo?.is_in_reverse_free_trial ||
    billingInfo?.is_fake_clinician_trial ||
    billingInfo?.evidence_billing?.is_in_free_trial
  );

  // Monthly subscriber: scribe-plus / clinician on monthly billing, not trialling.
  if (
    monthlyFlag &&
    !!userTier &&
    SUBSCRIBER_TIERS.includes(userTier) &&
    billingInfo?.plan_cycle === Period.ONE_MONTH &&
    !isTrialling
  ) {
    return 'monthly_subscriber';
  }

  // Yearly subscriber: same tiers, yearly billing, not trialling.
  if (
    yearlyFlag &&
    !!userTier &&
    SUBSCRIBER_TIERS.includes(userTier) &&
    billingInfo?.plan_cycle === Period.ONE_YEAR &&
    !isTrialling
  ) {
    return 'yearly_subscriber';
  }

  // Free / trialling: FREE tier or any active trial state.
  if (freeFlag && (userTier === SubscriptionTier.FREE || isTrialling)) {
    return 'free_user';
  }

  return null;
}

/** Boolean convenience for surfaces that just need to know "show or not". */
export function useIsRemoteIntroPromoEligible(host: RemoteIntroPromoHost): boolean {
  return useRemoteIntroPromoEligibility(host) !== null;
}
